import React, { useState, useEffect, useRef } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { Typography, Button, TextareaAutosize } from "@mui/material";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import { useDropzone } from "react-dropzone";
import deleteIcon from "../../assets/images/hq-delete.svg";
import pdfFileIcon from "../../assets/images/file-pdf.svg";
import wordFileIcon from "../../assets/images/file-word.svg";
import genericFileIcon from "../../assets/images/file-generic.svg";
import { extractValueFromCookie, handleSpaces } from "../helpers/helper-data";
import { danger, success } from "../helpers/message-box";
import uploadIcon from "../../assets/images/hq-upload.svg";
import Loader from "../Loader";
import ApiCalls from "../../api/customAxios";
import { enterpriseApiUrl } from "../../config/service-urls";
import Input from "@mui/material/Input";
import {
    FEEDBACK_ATTACHMENT_HELP_TEXT,
    FEEDBACK_ATTACHMENT_MESSAGES,
    FEEDBACK_MAX_FILES,
    formatFeedbackFileSize,
    getFeedbackBrowserLabel,
    getFeedbackFileKind,
    getFeedbackPageContext,
    splitFeedbackFilesBySize,
    uploadFeedbackFileToPresignedUrl,
    validateFeedbackFileSelection,
} from "../helpers/feedback-attachment.util";

/** Matches the enterprise-web feedback popup toast copy. */
const FEEDBACK_SUCCESS_TOAST =
    "Thank you for helping us improve your application experience.";

const FEEDBACK_FILE_ICONS = {
    pdf: pdfFileIcon,
    word: wordFileIcon,
    default: genericFileIcon,
};

const FEEDBACK_DROPZONE_ACCEPT = {
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "application/pdf": [".pdf"],
    "application/msword": [".doc"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    "application/zip": [".zip"],
    "application/x-zip-compressed": [".zip"],
    "video/mp4": [".mp4"],
    "video/quicktime": [".mov"],
    "video/webm": [".webm"],
};

function getFeedbackRequestConfig() {
    const enterpriseId =
        localStorage.getItem("enterprise_id") ||
        extractValueFromCookie("enterprise_id", document.cookie) ||
        localStorage.getItem("client_name");
    return enterpriseId ? { headers: { "x-enterprise-id": enterpriseId } } : {};
}
function getOperatingSystem() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    if (/windows phone/i.test(userAgent)) {
        return "Windows Phone";
    }
    if (/win64|wow64/i.test(userAgent)) {
        return "Windows 64-bit";
    }
    if (/win32/i.test(userAgent)) {
        return "Windows 32-bit";
    }
    if (/android/i.test(userAgent)) {
        return "Android";
    }
    if (/iphone|ipad|ipod/i.test(userAgent)) {
        return "iOS";
    }
    if (/mac/i.test(userAgent)) {
        return "Mac OS";
    }
    if (/linux/i.test(userAgent)) {
        return "Linux";
    }
    return navigator.platform || null;
}

function FeedBackModal(props) {
    const { open, onClose } = props;
    const appVersion = process.env.REACT_APP_APP_VERSION;
    const [review, setReview] = useState("");
    const [ipAddress, setIpAddress] = useState(null);
    const [browserWidth, setBrowserWidth] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [browserHeight, setBrowserHeight] = useState(null);
    const [subject, setSubject] = useState("");
    const [nameError, setNameError] = useState("");
    const [selectedFiles, setSelectedFiles] = useState([]);
    const operatingSystem = getOperatingSystem();
    /**
     * After a successful large-file presign+S3 upload, reuse on retry so we do not
     * create another feedback draft row (AC: prevent duplicate records).
     */
    const draftUploadRef = useRef({ feedbackId: null, attachments: [] });

    const clearDraftUploadState = () => {
        draftUploadRef.current = { feedbackId: null, attachments: [] };
    };

    const draftUploadsMatchLargeFiles = (largeFiles) => {
        const { feedbackId, attachments } = draftUploadRef.current;
        if (!feedbackId || attachments.length !== largeFiles.length) {
            return false;
        }
        const cachedNames = attachments
            .map((item) => item.filename.trim().toLowerCase())
            .sort();
        const currentNames = largeFiles.map((file) => file.name.trim().toLowerCase()).sort();
        return cachedNames.every((name, index) => name === currentNames[index]);
    };

    useEffect(() => {
        if (open) {
            const fetchIpAddress = async () => {
                try {
                    const response = await fetch("https://api.ipify.org/?format=json");
                    const data = await response.json();
                    setIpAddress(data.ip);
                } catch (error) {
                    console.error(error);
                }
            };
            fetchIpAddress();
        }
    }, [open]);

    useEffect(() => {
        const handleResize = () => {
            setBrowserWidth(window.innerWidth);
            setBrowserHeight(window.innerHeight);
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [open]);

    const handleDroppedFiles = (droppedFiles) => {
        let nextFiles = selectedFiles;
        droppedFiles.forEach((file) => {
            const validation = validateFeedbackFileSelection(file, nextFiles);
            if (!validation.valid) {
                danger(validation.message);
                return;
            }
            nextFiles = [...nextFiles, file];
        });
        if (nextFiles !== selectedFiles) {
            setSelectedFiles(nextFiles);
            clearDraftUploadState();
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: handleDroppedFiles,
        onDropRejected: () => danger(FEEDBACK_ATTACHMENT_MESSAGES.unsupportedFormat),
        accept: FEEDBACK_DROPZONE_ACCEPT,
        disabled: selectedFiles.length >= FEEDBACK_MAX_FILES,
    });

    const buildFeedbackPayload = (preuploadedAttachments = [], feedbackId) => {
        const pageContext = getFeedbackPageContext(window.location.pathname, window.location.href);
        const payload = {
            subject: subject.trim(),
            message: review.trim(),
            browser: getFeedbackBrowserLabel(),
            app_version: appVersion,
            platform: operatingSystem,
            module: pageContext.module,
            page_url: pageContext.page_url,
            ip_address: ipAddress,
            resolution: `${browserWidth}x${browserHeight}`,
            application: "Career Portal",
            userId: localStorage.getItem("client_name"),
            enterpriseName: localStorage.getItem("MSPName")
                ? localStorage.getItem("MSPName")
                : localStorage.getItem("enterprise_name"),
        };
        if (localStorage.getItem("MSPName") != null) {
            payload.mspEnterpriseName = localStorage.getItem("enterprise_name");
        }
        if (feedbackId) {
            payload.feedback_id = feedbackId;
        }
        if (preuploadedAttachments.length) {
            payload.preuploaded_attachments = preuploadedAttachments;
        }
        return payload;
    };

    const uploadLargeAttachments = async (files, feedbackPayload) => {
        const presignResponse = await ApiCalls.post(
            `${enterpriseApiUrl}feedback/attachments/presign`,
            {
                ...feedbackPayload,
                files: files.map((file) => ({
                    filename: file.name,
                    content_type: file.type || "application/octet-stream",
                    size: file.size,
                })),
            },
            getFeedbackRequestConfig()
        );

        const uploads = (presignResponse?.data ?? presignResponse)?.uploads ?? [];
        const feedbackId = (presignResponse?.data ?? presignResponse)?.feedback_id;
        if (!feedbackId || uploads.length !== files.length) {
            throw new Error("Failed to prepare upload URLs for large attachments.");
        }

        const preuploaded = [];
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const slot = uploads[i];
            await uploadFeedbackFileToPresignedUrl(
                slot.upload_url,
                file,
                slot.content_type || file.type || "application/octet-stream"
            );
            preuploaded.push({
                s3Key: slot.s3Key,
                filename: slot.filename || file.name,
                mimetype: slot.content_type || file.type || "application/octet-stream",
            });
        }
        return {
            feedbackId,
            attachments: preuploaded,
        };
    };

    const prepareFeedbackFormData = async () => {
        const { multipartFiles, presignFiles } = splitFeedbackFilesBySize(selectedFiles);
        let feedbackId;
        let preuploadedAttachments = [];

        if (presignFiles.length > 0) {
            if (draftUploadsMatchLargeFiles(presignFiles)) {
                feedbackId = draftUploadRef.current.feedbackId;
                preuploadedAttachments = draftUploadRef.current.attachments;
            } else {
                const uploaded = await uploadLargeAttachments(presignFiles, buildFeedbackPayload());
                feedbackId = uploaded.feedbackId;
                preuploadedAttachments = uploaded.attachments;
                draftUploadRef.current = {
                    feedbackId: uploaded.feedbackId,
                    attachments: uploaded.attachments,
                };
            }
        }

        const formData = new FormData();
        formData.append(
            "feedback",
            JSON.stringify(buildFeedbackPayload(preuploadedAttachments, feedbackId))
        );
        multipartFiles.forEach((file) => {
            formData.append("files", file);
        });
        return formData;
    };

    const resetForm = () => {
        setReview("");
        setSubject("");
        setSelectedFiles([]);
        setNameError("");
        clearDraftUploadState();
    };

    const handleSubmit = async () => {
        if (!subject?.trim() || !review?.trim() || nameError) {
            return;
        }
        setIsLoading(true);
        try {
            const formData = await prepareFeedbackFormData();
            await ApiCalls.post(
                `${enterpriseApiUrl}feedback`,
                formData,
                getFeedbackRequestConfig()
            );
            success(FEEDBACK_SUCCESS_TOAST);
            resetForm();
            onClose();
        } catch (error) {
            const message =
                error?.message ||
                error?.error ||
                "Something went wrong. Please try again.";
            danger(typeof message === "string" ? message : "Something went wrong. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteImage = (id) => {
        setSelectedFiles((prev) => prev.filter((_, index) => index !== id));
        clearDraftUploadState();
    };

    const handleModalClose = () => {
        resetForm();
        onClose();
    };

    if (isLoading) {
        return <Loader />;
    }

    return (
        <Modal
            open={open}
            onClose={handleModalClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box className="modal-content align-left inputcontrasts feedback-width">
                <Grid container id="modal-title">
                    <Grid item xs={11}>
                        <Typography
                            className="f-16 font-weight-600 gray9 modalTitle black-heading-color p-l-5"
                            align="left"
                        >
                            Feedback
                        </Typography>
                    </Grid>
                    <Grid item xs={1} style={{ marginTop: "-10px" }}>
                        <Typography align="right" className="sub-text-color" onClick={handleModalClose}>
                            <CloseIcon className="cursor-hover" />
                        </Typography>
                    </Grid>
                </Grid>
                <Grid id="feedback-modal-description">
                    <Grid item lg={6} md={6} xs={6}>
                        <Box className="m-b-model-label">
                            <Typography className="f-14">
                                Subject<span className="text-black">*</span>{" "}
                            </Typography>
                            <Input
                                type="text"
                                id="overview"
                                name="overview"
                                value={subject}
                                inputProps={{
                                    maxLength: 128,
                                }}
                                onChange={(event) => {
                                    setSubject(event.target.value);
                                    if (event.target.value.length > 128) {
                                        setNameError("Exceeded maximum character length of 128.");
                                    } else {
                                        setNameError("");
                                    }
                                }}
                                onKeyPress={handleSpaces}
                                className={`form-control input-group m-b-0 inputcontrasts ${
                                    nameError ? "is-invalid" : ""
                                }`}
                                margin="normal"
                                placeholder="Enter subject"
                            />
                        </Box>
                        <span className="danger-color error-msg"> {nameError}</span>
                    </Grid>
                    <Grid item xs={12} className="mt-20">
                        <Typography className="f-14 mb-5">
                            Tell us about your experience*
                        </Typography>
                        <Typography className="input-focus-none placeholder-text">
                            <TextareaAutosize
                                minRows={2}
                                maxRows={4}
                                aria-label="maximum height"
                                className="form-control input-group inputcontrasts height-150"
                                maxLength={256}
                                inputProps={{ maxLength: 256 }}
                                onChange={(e) => {
                                    if (e.target.value.length === 1) {
                                        setReview(e.target.value.toUpperCase());
                                        return;
                                    }
                                    setReview(e.target.value);
                                }}
                                onKeyPress={handleSpaces}
                                name="review"
                                id="review"
                                value={review}
                                placeholder="Enter feedback"
                            />
                            <Typography
                                align="right"
                                className="sub-text-color sub-text-font"
                            >
                                {review?.length ? review?.length : 0}/256
                            </Typography>
                        </Typography>
                        <Box>
                            <Typography className="f-14 m-b-5 m-t-10">Attachments</Typography>
                            {selectedFiles?.length > 0 && (
                                <Typography
                                    className="d-flex align-center m-b-10"
                                    style={{ flexDirection: "column" }}
                                >
                                    {selectedFiles.map((image, key) => {
                                        const fileIcon =
                                            FEEDBACK_FILE_ICONS[getFeedbackFileKind(image)];

                                        return (
                                            <Grid
                                                item
                                                lg={12}
                                                md={12}
                                                xs={12}
                                                key={`${image.name}-${key}`}
                                                className="d-flex mt-5  justify-between file-cards p-0 m-l-5"
                                                style={{ width: "100%" }}
                                            >
                                                <Grid
                                                    item
                                                    lg={10}
                                                    md={10}
                                                    xs={10}
                                                    className="d-flex align-center"
                                                >
                                                    <img
                                                        src={fileIcon}
                                                        alt=""
                                                        className="m-r-10 ml-10"
                                                        width="24px"
                                                        height="24px"
                                                    />
                                                    <Box style={{ minWidth: 0 }}>
                                                        <p className="gray9 mb-0 f-14 textEllipse">
                                                            {image?.name}
                                                        </p>
                                                        <p className="f-12 mt-0 sub-text-color">
                                                            {formatFeedbackFileSize(image?.size)} MB
                                                        </p>
                                                    </Box>
                                                </Grid>
                                                <Grid
                                                    item
                                                    lg={2}
                                                    md={2}
                                                    xs={2}
                                                    className="font-13 align-item-right d-flex"
                                                >
                                                    <span
                                                        tabIndex={0}
                                                        onKeyPress={() => handleDeleteImage(key)}
                                                        className="align-center d-flex sub-text-font m-r-10 cursor-hover gray7"
                                                        onClick={() => handleDeleteImage(key)}
                                                    >
                                                        <img
                                                            className="sub-text-font"
                                                            src={deleteIcon}
                                                            alt="delete icon"
                                                            width="18px"
                                                            height="18px"
                                                        />
                                                    </span>
                                                </Grid>
                                            </Grid>
                                        );
                                    })}
                                </Typography>
                            )}
                            <Box
                                {...getRootProps()}
                                sx={{
                                    border: "1px dashed #98a2b3",
                                    borderRadius: "8px",
                                    padding: "32px 16px",
                                    textAlign: "center",
                                    marginTop: "5px",
                                    backgroundColor: isDragActive ? "#fafafa" : "#fff",
                                    cursor:
                                        selectedFiles.length >= FEEDBACK_MAX_FILES
                                            ? "not-allowed"
                                            : "pointer",
                                    opacity: selectedFiles.length >= FEEDBACK_MAX_FILES ? 0.6 : 1,
                                }}
                            >
                                <input {...getInputProps()} />
                                <img src={uploadIcon} width="48px" height="48px" alt="upload" />
                                <Typography className="f-16 gray9 m-t-5">
                                    Click or drag file to this area to upload
                                </Typography>
                                <Typography className="f-14 sub-text-color m-t-5">
                                    {FEEDBACK_ATTACHMENT_HELP_TEXT}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
                <Grid id="modal-footer" className="justify-end d-flex">
                    <Typography className="d-flex align-center">
                        <Button
                            className="btn primary-button"
                            disabled={!subject?.trim() || !!nameError || !review?.trim()}
                            onClick={handleSubmit}
                            tabIndex={0}
                            disableRipple="true"
                            aria-label="button"
                        >
                            Submit
                        </Button>
                    </Typography>
                </Grid>
            </Box>
        </Modal>
    );
}

export default FeedBackModal;
